/**
 * Order-sheet prefill from the school's own order history (v2 "Pre-fill
 * meal selection").
 *
 * When the school's `prefillMealSelectionEnabled` toggle is on, an
 * orderable, unordered day seeds each (item × order column) cell with the
 * school's TYPICAL quantity for that weekday — the mode of what they
 * actually ordered over the last 8 weeks (`GET /typical-quantities`).
 * Cells with no history stay blank. No headcount × rate math.
 *
 * Pre-filled lines carry `suggested: true`; the first user edit of a
 * cell replaces the line without the flag. Reset (the day-card header
 * action) drops the day's draft and lets this seeding re-run fresh.
 *
 * Pure (no React) — unit-tested in `client/test/lib/school-orders/`.
 */

import type { ComponentKind } from '@/api/location-settings'
import { COMPONENT_KINDS } from '@/api/location-settings'
import type { Mealtime, TypicalQuantityCell } from '@/api/school-orders'

import { cartLineKey, type SchoolCartLine } from './cart'
import { isItemAllowedForColumn, type ItemColumnScope, type OrderColumn } from './columns'

/** Pre-normalized menu-item shape (the panel maps `ActiveMenuItem` into this). */
export interface PrefillMenuItem {
  id: string
  itemKind: 'BUNDLE' | 'COMPONENT'
  /** Narrowed `component.kind`; null for bundles or unknown kinds. */
  componentKind: ComponentKind | null
  /** System-milk marker off the menus wire; null = ordinary item. */
  lineupMilkOptionCode: string | null
  name: string
  imageUri: string | null
  unitPriceCents: number | null
  currency: string | null
  /**
   * Column hard-gate inputs (menu grade band + field-trip scopes) — a
   * cell the grid dashes out must never be seeded, even when history
   * from before the gate exists. Omitted = ungated.
   */
  columnScope?: ItemColumnScope
}

/** Narrow a wire `component.kind` string to the known enum, else null. */
export function narrowComponentKind(kind: string | null | undefined): ComponentKind | null {
  return kind && (COMPONENT_KINDS as readonly string[]).includes(kind) ? (kind as ComponentKind) : null
}

export interface ShouldPrefillDayArgs {
  /** The school's "Pre-fill meal selection" toggle. */
  prefillMealSelectionEnabled: boolean
  /** The day already has a placed order — cells show it read-only. */
  dayHasOrder: boolean
  /** Past date / deadline passed / explicit read-only view. */
  closed: boolean
  /** The week cart already holds lines for this day (added, not yet checked out). */
  hasCartLinesForDay: boolean
}

/**
 * Whether an expanded day should be seeded with suggestions. The "day
 * already has draft edits" and "already seeded this session" gates live
 * in the draft store's `seedSuggestedDay`, not here.
 */
export function shouldPrefillDay(args: ShouldPrefillDayArgs): boolean {
  return args.prefillMealSelectionEnabled && !args.dayHasOrder && !args.closed && !args.hasCartLinesForDay
}

/** 0-6 weekday (`getUTCDay` convention) for a `YYYY-MM-DD` day key. */
export function weekdayOfDateKey(dateKey: string): number {
  return new Date(`${dateKey}T00:00:00Z`).getUTCDay()
}

export interface BuildTypicalPrefillLinesArgs {
  locationId: string
  /** `YYYY-MM-DD` day key (cart-line date segment). */
  dateKey: string
  mealtimes: readonly Mealtime[]
  itemsByMealtime: ReadonlyMap<Mealtime, readonly PrefillMenuItem[]>
  columns: readonly OrderColumn[]
  /** The school's typical-quantity history cells (all weekdays). */
  cells: readonly TypicalQuantityCell[]
}

/**
 * Suggested cart lines for a day, from the school's typical quantities
 * for that weekday. Only (item × column) cells with history produce a
 * line — everything else stays blank.
 */
export function buildTypicalPrefillLines(args: BuildTypicalPrefillLinesArgs): SchoolCartLine[] {
  const weekday = weekdayOfDateKey(args.dateKey)
  const byCell = new Map<string, number>()
  for (const cell of args.cells) {
    if (cell.weekday === weekday && cell.quantity > 0) {
      byCell.set(`${cell.menuItemId}::${cell.mealtime}::${cell.columnId}`, cell.quantity)
    }
  }
  const lines: SchoolCartLine[] = []
  for (const mealtime of args.mealtimes) {
    for (const item of args.itemsByMealtime.get(mealtime) ?? []) {
      for (const column of args.columns) {
        // Never seed a cell the order grid gates off (menu grade band /
        // field-trip scope) — pre-gate history would otherwise create
        // invisible draft lines the server rejects at checkout.
        if (item.columnScope && !isItemAllowedForColumn(item.columnScope, column)) {
          continue
        }
        const quantity = byCell.get(`${item.id}::${mealtime}::${column.columnId}`)
        if (!quantity) {
          continue
        }
        lines.push({
          key: cartLineKey(args.dateKey, mealtime, column.columnId, item.id),
          locationId: args.locationId,
          date: args.dateKey,
          mealtime,
          columnId: column.columnId,
          gradeRangeId: column.gradeRangeId,
          groupDimension: column.groupDimension,
          groupValue: column.groupValue,
          menuItemId: item.id,
          name: item.name,
          imageUri: item.imageUri,
          unitPriceCents: item.unitPriceCents,
          currency: item.currency,
          quantity,
          suggested: true,
          // History can hold hand-typed system-milk orders from before the
          // derived-milk feature — seed them with their identity so the
          // meal counters never mistake them for meals.
          ...(item.lineupMilkOptionCode != null ? { systemMilk: true } : {}),
        })
      }
    }
  }
  return lines
}
